import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    model,
    OnDestroy,
    signal,
    ViewEncapsulation
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AixDataTestingDirective } from '../../directives/data-testing/data-testing.directive';
import { isArray } from 'lodash-es';

@Component({
    selector: 'aix-show-more',
    templateUrl: './aix-show-more.html',
    styleUrls: ['./aix-show-more.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AixDataTestingDirective]
})
export class AixShowMoreComponent implements OnDestroy {
    title = input<string>();
    filters = input<any[]>();
    elementView = input<HTMLElement>();

    activeFilter = model.required<any[]>();

    numberShowList = signal(7);
    showMore = signal(false);

    /**
     * Bumped whenever the list's own height changes, to re-run the measurements below.
     *
     * They read the DOM from inside a computed, so without this they are pinned to
     * whatever the layout was the last time filters or showMore changed. Selecting a
     * filter bolds its label (u-fw600 in aix-filter-list), which can push a long one onto
     * a second line -- the list grows, the measured height does not, and the caller's
     * overflow: hidden swallows the entries past it.
     */
    private contentResized = signal(0);

    hasMore = computed(() => {
        this.contentResized();

        const nativeElement: HTMLElement = (<HTMLElement>this.elementView())
            ?.children[0] as HTMLElement;

        if (this.filters()) {
            return this.calculateTotalLines(nativeElement) > this.numberShowList();
        }

        return false;
    });
    pixelsToShow = computed(() => {
        this.contentResized();

        // Nothing is being collapsed, so there is no height to hold the list to. Pinning
        // one anyway only creates a way to clip it.
        if (!this.hasMore() || this.showMore()) {
            return 'auto';
        }

        const nativeElement: HTMLElement = (<HTMLElement>this.elementView())
            ?.children[0] as HTMLElement;

        return `${this.calculateMinimizedHeight(nativeElement)}px`;
    });

    showMoreSelectedNumber = 0;

    subscriptions: Subscription[] = [];

    constructor() {
        effect(() => {
            this.showSelectedNumber();
        });

        effect(onCleanup => {
            const list = this.resizeTarget();

            if (!list || typeof ResizeObserver === 'undefined') {
                return;
            }

            // Safe to observe: this sits inside the caller's clipping box, so its height
            // is its own content and does not move when pixelsToShow changes the box
            // around it.
            const observer = new ResizeObserver(() =>
                this.contentResized.update(count => count + 1)
            );
            observer.observe(list);
            onCleanup(() => observer.disconnect());
        });
    }

    /**
     * The element to watch for the list growing.
     *
     * NOT elementView() itself -- that is the caller's clipping box, whose height *is*
     * pixelsToShow, so observing it would feed straight back into itself.
     *
     * NOT its first child either, which is the list component's host element
     * (aix-filter-list, aix-checkbox-list). Neither declares a display, so both are
     * non-replaced inline elements, and ResizeObserver reports a zero content box for
     * those -- it fires once on observe and then never again, however much the list
     * grows. aix-data-grid sets `display: block` on its own host for the same reason.
     *
     * The UL every caller renders is a block box that tracks the list's content, and it
     * is the node calculateTotalLines and calculateMinimizedHeight already single out.
     * Falling back to the host keeps the non-UL shape those two also handle no worse off
     * than before.
     */
    private resizeTarget(): Element | undefined {
        const host = this.elementView()?.children[0];

        if (!host) {
            return undefined;
        }

        return Array.from(host.children).find(child => child.nodeName === 'UL') ?? host;
    }

    showSelectedNumber() {
        this.showMoreSelectedNumber = 0;
        if (this.activeFilter() && this.filters()) {
            const activeFilter = isArray(this.activeFilter())
                ? this.activeFilter()
                : [this.activeFilter()];
            activeFilter.forEach(f => {
                const pos = this.filters()
                    ?.map(e => e.id)
                    .indexOf(f.id);
                if (pos && pos + 1 > this.numberShowList()) {
                    this.showMoreSelectedNumber++;
                }
            });
        }
    }

    change() {
        this.showMore.set(!this.showMore());
        this.showSelectedNumber();
    }

    calculateTotalLines(nativeElement: Element) {
        let totalLines = 0;
        if (nativeElement && nativeElement.childNodes && nativeElement.childNodes.length > 0) {
            nativeElement.childNodes.forEach(node => {
                if (node.nodeName === 'UL') {
                    // UL Elements are coming in with a last child node of a #comment, so this subtracts 1 from the length
                    totalLines += node.childNodes.length - 1;
                } else if (node.nodeName !== '#comment' && !!node.textContent) {
                    totalLines++;
                }
            });
        }
        return totalLines;
    }

    calculateMinimizedHeight(nativeElement: Element) {
        let maxHeight = 0;
        if (nativeElement && nativeElement.childNodes && nativeElement.childNodes.length > 0) {
            nativeElement.childNodes.forEach(node => {
                if (node.nodeName === 'UL') {
                    for (let i = 0; i < this.numberShowList(); i++) {
                        const elem = node.childNodes[i] as HTMLElement;
                        if (elem?.nodeName !== '#comment' && !!elem?.textContent) {
                            maxHeight +=
                                elem.offsetHeight +
                                parseInt(getComputedStyle(elem).marginTop) +
                                parseInt(getComputedStyle(elem).marginBottom);
                        }
                    }
                } else if (node.nodeName !== '#comment' && !!node.textContent) {
                    maxHeight =
                        (<HTMLElement>node).offsetHeight +
                        parseInt(getComputedStyle(<HTMLElement>node).marginTop) +
                        parseInt(getComputedStyle(<HTMLElement>node).marginBottom);
                }
            });
        }
        return maxHeight;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
